/**
 * Resumable chunked uploader. Each file is created server-side, then sent in
 * chunks with an explicit Upload-Offset. Network failures retry with backoff
 * and resume from the server's committed offset, so nothing is re-sent twice.
 */
import { useSyncExternalStore } from 'react';
import type { AddMasterBody, MasterConflict, UploadDTO, VersionUploadBody } from '@prism/contracts';
import { api, ApiError } from '../api/client';
import { holdWakeLock } from '../lib/wake-lock';

type UploadState =
  | 'queued' | 'uploading' | 'paused' | 'retrying' | 'finalizing' | 'done' | 'duplicate' | 'master_exists' | 'error' | 'cancelled';

export interface UploadItem {
  localId: string;
  file: File;
  uploadId: string | null;
  state: UploadState;
  sent: number;
  total: number;
  rate: number; // bytes/s
  error: string | null;
  assetId: string | null;
  duplicate: { existingAssetId: string; existingTitle: string } | null;
  attempts: number;
  /** While retrying (1.6.4): when the next attempt starts (Date.now() ms), for a "resuming in N s" countdown. */
  retryAt?: number | null;
  /** A placeholder this file fills instead of becoming a new asset. */
  placeholder: PlaceholderTarget | null;
  /** "Add master…" (1.2.0): the file becomes a further master of this asset's current version, not a new asset. */
  master: MasterTarget | null;
  /** The reason a master was refused (DIFFERENT_CONTENT, MASTER_EXISTS, UNKNOWN_VARIANT), shown in the tray. */
  errorCode?: string | null;
  /**
   * A completed master upload: added (with its label), kept (onConflict skip found one already: added false), or
   * pending (202: the file is still being checked after 90 s; it appears on the Masters tab once verified).
   */
  masterOutcome?: { kind: 'added' | 'kept' | 'pending'; label: string | null } | null;
  /** "Upload… → A new version of this asset" (1.2.2): the file becomes the asset's next version. */
  version?: VersionTarget | null;
  /** A completed new version: its number, and a warning when it is the same file as an earlier version. */
  versionOutcome?: { number: number; warning: string | null } | null;
}

export interface PlaceholderTarget { id: string; assetCode: string; title: string }

/** Where a new-version upload goes (POST /api/v1/assets/:id/versions/uploads, VersionUploadBody; 1.2.2). */
export interface VersionTarget {
  assetId: string;
  assetCode: string;
  title: string;
  /** The version the uploader saw as current: another version arriving meanwhile refuses it (VERSION_CONFLICT). */
  expectedVersion: number;
}

/** Where an "Add master…" upload goes (POST /api/v1/assets/:id/masters/uploads, AddMasterBody). */
export interface MasterTarget {
  assetId: string;
  assetCode: string;
  title: string;
  /** null: Programme. */
  variantId: string | null;
  variantName: string;
  /** A master of the same variant and format exists: refuse (the tray then asks), replace it, or skip this file. */
  onConflict: MasterConflict;
}

type Listener = () => void;

/** How often a chunk's progress may update the item (1.6.4): about four times a second. */
export const PROGRESS_INTERVAL_MS = 250;

/** One chunk's answer: the bytes the server now holds, or (409) the offset it holds instead of the one sent. */
export type ChunkResult = { kind: 'ok'; receivedBytes: number } | { kind: 'offset'; receivedBytes: number };

/**
 * Sends one chunk by PATCH with XMLHttpRequest (1.6.4), so upload.onprogress reports the bytes on their way: fetch
 * gave no progress until a whole chunk (8 MB) was through. An abort rejects with an AbortError; a network failure
 * with ApiError(0, NETWORK), retried like before; a 409 with the server's offset resolves to it; any other failure
 * becomes an ApiError with the server's code.
 */
export function sendChunk(url: string, offset: number, body: Blob, signal: AbortSignal, onProgress: (loaded: number) => void): Promise<ChunkResult> {
  return new Promise((resolve, reject) => {
    const abortError = () => new DOMException('The upload was stopped', 'AbortError');
    if (signal.aborted) { reject(abortError()); return; }
    const xhr = new XMLHttpRequest();
    const onAbort = () => xhr.abort();
    const done = () => signal.removeEventListener('abort', onAbort);
    xhr.open('PATCH', url);
    xhr.withCredentials = true;
    xhr.setRequestHeader('content-type', 'application/offset+octet-stream');
    xhr.setRequestHeader('upload-offset', String(offset));
    xhr.upload.onprogress = (e: ProgressEvent) => onProgress(e.loaded);
    xhr.onabort = () => { done(); reject(abortError()); };
    xhr.onerror = () => { done(); reject(new ApiError(0, 'NETWORK', 'PRISM is unreachable. Check your connection; we will keep retrying.')); };
    xhr.onload = () => {
      done();
      let parsed: { receivedBytes?: number; error?: { code?: string; message?: string; details?: Record<string, unknown> } } | null = null;
      try { parsed = xhr.responseText ? JSON.parse(xhr.responseText) : null; } catch { parsed = null; }
      if (xhr.status === 409) {
        const serverOffset = parsed?.error?.details?.receivedBytes;
        if (typeof serverOffset === 'number') { resolve({ kind: 'offset', receivedBytes: serverOffset }); return; }
        reject(new ApiError(409, parsed?.error?.code ?? 'CONFLICT', parsed?.error?.message ?? 'Upload conflict', parsed?.error?.details));
        return;
      }
      if (xhr.status < 200 || xhr.status >= 300) {
        reject(new ApiError(xhr.status, parsed?.error?.code ?? `HTTP_${xhr.status}`, parsed?.error?.message ?? `Upload failed (${xhr.status})`, parsed?.error?.details));
        return;
      }
      if (typeof parsed?.receivedBytes !== 'number') { reject(new ApiError(0, 'BAD_RESPONSE', 'The server gave no upload offset')); return; }
      resolve({ kind: 'ok', receivedBytes: parsed.receivedBytes });
    };
    signal.addEventListener('abort', onAbort);
    xhr.send(body);
  });
}

/** "about 2 min left", from the bytes left and the rate (bytes/s); '' while there is no rate yet. */
export function timeLeft(remaining: number, rate: number): string {
  if (!(rate > 0) || remaining <= 0) return '';
  const s = remaining / rate;
  if (s < 10) return 'a few seconds left';
  if (s < 60) return `about ${Math.ceil(s / 10) * 10} s left`;
  if (s < 3600) return `about ${Math.round(s / 60)} min left`;
  const h = Math.floor(s / 3600);
  const m = Math.round((s % 3600) / 60);
  return m && h < 10 ? `about ${h} h ${m} min left` : `about ${Math.round(s / 3600)} h left`;
}

/** A rate as "3.1 MB/s" (decimal megabytes, like the file sizes a phone shows). */
export function rateLabel(rate: number): string {
  if (!(rate > 0)) return '';
  if (rate < 1e6) return `${Math.max(1, Math.round(rate / 1e3))} kB/s`;
  return `${(rate / 1e6).toFixed(rate < 1e7 ? 1 : 0)} MB/s`;
}

/** An upload card's progress line: "42 % · 3.1 MB/s · about 2 min left". */
export function progressLine(sent: number, total: number, rate: number): string {
  const pct = Math.floor((sent / Math.max(1, total)) * 100);
  return [`${pct} %`, rateLabel(rate), timeLeft(total - sent, rate)].filter(Boolean).join(' · ');
}

/** The states an upload is still running in (queued counts: it starts by itself). */
export const RUNNING_STATES: ReadonlyArray<UploadState> = ['queued', 'uploading', 'retrying', 'finalizing'];

export class Uploader {
  private items: UploadItem[] = [];
  private listeners = new Set<Listener>();
  private active = 0;
  private readonly concurrency = 2;
  private controllers = new Map<string, AbortController>();
  onAssetCreated: ((assetId: string) => void) | null = null;
  /** Set while a placeholder's page is open: a file dropped anywhere goes into it. */
  defaultTarget: PlaceholderTarget | null = null;

  subscribe = (l: Listener) => {
    this.listeners.add(l);
    return () => this.listeners.delete(l);
  };
  snapshot = () => this.items;

  private update(localId: string, patch: Partial<UploadItem>) {
    this.items = this.items.map((i) => (i.localId === localId ? { ...i, ...patch } : i));
    this.notify();
  }
  private releaseWakeLock: (() => void) | null = null;
  /** Tells the listeners, and keeps the screen awake while any upload runs (1.6.4: iOS pauses a page when it locks). */
  private notify() {
    const running = this.items.some((i) => RUNNING_STATES.includes(i.state));
    if (running && !this.releaseWakeLock) this.releaseWakeLock = holdWakeLock();
    else if (!running && this.releaseWakeLock) { this.releaseWakeLock(); this.releaseWakeLock = null; }
    for (const l of this.listeners) l();
  }
  private get(localId: string) {
    return this.items.find((i) => i.localId === localId)!;
  }

  add(files: File[], opts: { placeholder?: PlaceholderTarget; master?: MasterTarget; version?: VersionTarget } = {}) {
    // One placeholder takes one file; any further files become new assets. A master target takes one file too
    // (one master per variant and format), and the others are not uploaded; so does a new version.
    const target = opts.master ?? opts.version ?? null;
    const list = target ? files.slice(0, 1) : files;
    const fresh = list.map<UploadItem>((file, i) => ({
      localId: crypto.randomUUID(), file, uploadId: null, state: 'queued', sent: 0, total: file.size, rate: 0,
      error: null, assetId: target?.assetId ?? null, duplicate: null, attempts: 0, placeholder: i === 0 && !target ? opts.placeholder ?? null : null,
      master: opts.master ?? null, version: opts.version ?? null, errorCode: null,
    }));
    this.items = [...this.items, ...fresh];
    this.notify();
    this.pump();
  }

  /** Choose (or clear) the placeholder a file fills. Allowed until the upload is being registered; applied when it completes. */
  setPlaceholder(localId: string, placeholder: PlaceholderTarget | null) {
    if (['finalizing', 'done', 'cancelled'].includes(this.get(localId).state) || this.get(localId).master || this.get(localId).version) return;
    this.update(localId, { placeholder });
  }

  private pump() {
    while (this.active < this.concurrency) {
      const next = this.items.find((i) => i.state === 'queued');
      if (!next) return;
      this.active++;
      this.update(next.localId, { state: 'uploading' });
      void this.run(next.localId).finally(() => {
        this.active--;
        this.pump();
      });
    }
  }

  private async run(localId: string): Promise<void> {
    const ctrl = new AbortController();
    this.controllers.set(localId, ctrl);
    try {
      let item = this.get(localId);
      if (!item.uploadId) {
        const m = item.master;
        const v = item.version;
        const created = m
          ? await api<UploadDTO>(`/api/v1/assets/${m.assetId}/masters/uploads`, {
              method: 'POST',
              json: { filename: item.file.name, sizeBytes: item.file.size, variantId: m.variantId, onConflict: m.onConflict } satisfies AddMasterBody,
            })
          : v
          ? await api<UploadDTO>(`/api/v1/assets/${v.assetId}/versions/uploads`, {
              method: 'POST',
              json: { filename: item.file.name, sizeBytes: item.file.size, expectedVersion: v.expectedVersion } satisfies VersionUploadBody,
            })
          : await api<UploadDTO>('/api/v1/uploads', {
              method: 'POST',
              json: { filename: item.file.name, sizeBytes: item.file.size, mimeType: item.file.type || undefined, placeholderAssetId: item.placeholder?.id },
            });
        this.update(localId, { uploadId: created.id });
        item = this.get(localId);
      }
      const status = await api<UploadDTO>(`/api/v1/uploads/${item.uploadId}`);
      let offset = status.receivedBytes;
      const chunk = status.chunkSize;
      let windowStart = performance.now();
      let windowBytes = 0;
      let counted = offset; // bytes already counted into the rate window
      let lastShown = 0;
      const measure = (at: number) => {
        windowBytes += Math.max(0, at - counted);
        counted = at;
        const now = performance.now();
        const rate = windowBytes / Math.max(0.001, (now - windowStart) / 1000);
        if (now - windowStart > 3000) {
          windowStart = now;
          windowBytes = 0;
        }
        return { now, rate };
      };
      while (offset < item.file.size) {
        if (ctrl.signal.aborted) return;
        const cur = this.get(localId);
        if (cur.state === 'paused' || cur.state === 'cancelled') return;
        const end = Math.min(offset + chunk, item.file.size);
        const from = offset;
        const res = await sendChunk(`/api/v1/uploads/${item.uploadId}`, from, item.file.slice(from, end), ctrl.signal, (loaded) => {
          if (ctrl.signal.aborted) return;
          const at = Math.min(end, from + loaded);
          const { now, rate } = measure(at);
          if (now - lastShown < PROGRESS_INTERVAL_MS || this.get(localId).state !== 'uploading') return;
          lastShown = now;
          this.update(localId, { sent: at, rate });
        });
        if (res.kind === 'offset') {
          offset = res.receivedBytes;
          counted = offset;
          continue;
        }
        offset = res.receivedBytes;
        const { now, rate } = measure(offset);
        lastShown = now;
        this.update(localId, { sent: offset, rate, state: 'uploading', attempts: 0, error: null });
      }
      await this.complete(localId, 'ask');
    } catch (err) {
      if (ctrl.signal.aborted) return;
      const e = err as ApiError;
      const retryable = !(e instanceof ApiError) || e.status === 0 || e.status >= 500;
      const attempts = this.get(localId).attempts + 1;
      if (retryable && attempts <= 8) {
        const delay = Math.min(30_000, 1000 * 2 ** attempts);
        this.update(localId, { state: 'retrying', attempts, retryAt: Date.now() + delay, error: `Connection interrupted. Resuming in ${Math.round(delay / 1000)} s…` });
        setTimeout(() => {
          if (this.get(localId).state === 'retrying') {
            this.update(localId, { state: 'queued' });
            this.pump();
          }
        }, delay);
      } else {
        this.update(localId, { state: 'error', error: e.message, errorCode: e instanceof ApiError ? e.code : null });
      }
    } finally {
      this.controllers.delete(localId);
    }
  }

  async complete(localId: string, duplicatePolicy: 'ask' | 'allow') {
    const item = this.get(localId);
    this.update(localId, { state: 'finalizing', error: null, errorCode: null });
    try {
      // The target is sent at completion too, so it can still be changed while the bytes are on their way.
      // A master upload sends its conflict choice again: "Replace" in the tray after a MASTER_EXISTS completes with it.
      const json = item.master
        ? { duplicatePolicy: 'allow', onConflict: item.master.onConflict }
        : item.version
        ? { duplicatePolicy }
        : { duplicatePolicy, placeholderAssetId: item.placeholder?.id ?? null };
      // A master upload answers 201 {assetId, runId, masterId, added, replaced, master}, or 202 {pending, runId}; a new
      // version 201 {assetId, version, warning?}.
      const res = await api<{ assetId?: string; pending?: boolean; added?: boolean; master?: { label: string } | null; version?: number; warning?: string }>(`/api/v1/uploads/${item.uploadId}/complete`, { method: 'POST', json });
      const assetId = res?.assetId ?? item.master?.assetId ?? item.version?.assetId ?? null;
      const masterOutcome = item.master
        ? { kind: res?.pending ? 'pending' as const : res?.added === false ? 'kept' as const : 'added' as const, label: res?.master?.label ?? null }
        : null;
      const versionOutcome = item.version ? { number: res?.version ?? item.version.expectedVersion + 1, warning: res?.warning ?? null } : null;
      this.update(localId, { state: 'done', assetId, sent: item.total, masterOutcome, versionOutcome });
      if (assetId) this.onAssetCreated?.(assetId);
    } catch (err) {
      const e = err as ApiError;
      if (item.master && e.code === 'MASTER_EXISTS') {
        this.update(localId, { state: 'master_exists', error: e.message, errorCode: e.code });
      } else if (item.master) {
        // DIFFERENT_CONTENT, UNKNOWN_VARIANT…: the reason is shown; the file is not added.
        this.update(localId, { state: 'error', error: e.message, errorCode: e.code ?? null });
      } else if (e.code === 'DUPLICATE_CONTENT') {
        this.update(localId, {
          state: 'duplicate',
          duplicate: { existingAssetId: String(e.details?.existingAssetId), existingTitle: String(e.details?.existingTitle) },
        });
      } else {
        // A new version: VERSION_CONFLICT (another version arrived meanwhile), KIND_MISMATCH…, with the reason.
        this.update(localId, { state: 'error', error: e.message, errorCode: e.code ?? null });
      }
    }
  }

  importAnyway(localId: string) {
    void this.complete(localId, 'allow');
  }

  /** After MASTER_EXISTS: replace the existing master of that variant and format (it is kept until this one is verified). */
  replaceMaster(localId: string) {
    const item = this.get(localId);
    if (!item.master) return;
    this.update(localId, { master: { ...item.master, onConflict: 'replace' } });
    void this.complete(localId, 'allow');
  }

  pause(localId: string) {
    this.update(localId, { state: 'paused' });
    this.controllers.get(localId)?.abort();
  }

  resume(localId: string) {
    this.update(localId, { state: 'queued', error: null, attempts: 0 });
    this.pump();
  }

  async cancel(localId: string) {
    const item = this.get(localId);
    this.controllers.get(localId)?.abort();
    this.update(localId, { state: 'cancelled' });
    if (item.uploadId) await api(`/api/v1/uploads/${item.uploadId}`, { method: 'DELETE' }).catch(() => undefined);
  }

  clearFinished() {
    this.items = this.items.filter((i) => !['done', 'cancelled'].includes(i.state));
    this.notify();
  }
}

export const uploader = new Uploader();

export function useUploads(): UploadItem[] {
  return useSyncExternalStore(uploader.subscribe, uploader.snapshot);
}
